import { Capacitor } from '@capacitor/core';
import type { DeviceType } from './settingTypes';

/**
 * The kind of device a screen setting (zoom, notation display) is kept for.
 * The account keeps one value per kind, so a phone and a tablet each start
 * from their own value and a new phone starts from the player's phone value.
 */

/** The shorter side of a screen, in CSS pixels, below which it is a phone. */
export const PHONE_SHORT_SIDE_MAX = 600;

/** The shorter side, in CSS pixels, from which a touch screen counts as a desktop. */
export const TOUCH_DESKTOP_SHORT_SIDE_MIN = 1100;

export interface ScreenFacts {
  /** `ios`, `android` or `web` (Capacitor's platform). */
  platform: string;
  /** The screen's shorter side in CSS pixels. */
  shortSide: number;
  /** True when the primary pointer is a finger (no fine pointer). */
  touchFirst: boolean;
}

/**
 * The device type from the screen: the native apps are phones or tablets by
 * screen size; on the web a touch-first screen is a phone or tablet the same
 * way (a very large touch screen counts as a desktop), and anything with a
 * mouse is a desktop.
 */
export const deviceTypeFor = ({ platform, shortSide, touchFirst }: ScreenFacts): DeviceType => {
  if (!Number.isFinite(shortSide) || shortSide <= 0) {
    return platform === 'web' ? 'desktop' : 'phone';
  }
  const native = platform === 'ios' || platform === 'android';
  if (!native && !touchFirst) return 'desktop';
  if (shortSide < PHONE_SHORT_SIDE_MAX) return 'phone';
  if (!native && shortSide >= TOUCH_DESKTOP_SHORT_SIDE_MIN) return 'desktop';
  return 'tablet';
};

const readScreenFacts = (): ScreenFacts => {
  const screenSize =
    typeof window !== 'undefined' && window.screen
      ? Math.min(window.screen.width, window.screen.height)
      : Number.NaN;
  const touchFirst =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  return { platform: Capacitor.getPlatform(), shortSide: screenSize, touchFirst };
};

/**
 * This device's type. The screen's shorter side does not change with
 * rotation, so the answer is the same in portrait and landscape.
 */
export const currentDeviceType = (): DeviceType => deviceTypeFor(readScreenFacts());
